import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoComplete } from 'primeng/autocomplete';
import { Subject, Subscription, of } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficaClassificheService } from 'src/app/core/anagrafica-classifiche.service';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { AnagraficaQualificheService } from 'src/app/core/anagrafica-qualifiche.service';
import { AnagraficaSezioniService } from 'src/app/core/anagrafica-sezione.service';
import { AnagraficaStatiService } from 'src/app/core/anagrafica-stati.service';
import { AnagraficaUtentiService } from 'src/app/core/anagrafica-utenti.service';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { PosizioniService } from 'src/app/core/posizioni.service';
import { DocumentiListColumnNames } from 'src/app/shared/components/documenti-list/documenti-list.model';
import { PositionSelectComponent } from 'src/app/shared/components/position-select.component';
import { autocompleteFilter } from 'src/app/shared/helpers/autocompleteFilter.helper';
import { DocumentiFTSearchFilters, DocumentiFTSearchRequest, DocumentiFTSearchResponse, DocumentiSearchFilters, DocumentiSearchRequest, DocumentiSearchResponse, DocumentoSearchForm } from 'src/app/shared/models/documento-search.model';
import { Posizione } from 'src/app/shared/models/posizione.model';
import { Sezione } from 'src/app/shared/models/sezione.model';
import { Utente } from 'src/app/shared/models/utente.model';


@Component({
  selector: 'app-ricerca',
  templateUrl: './ricerca.component.html',
  styleUrls: ['./ricerca.component.scss']
})
export class RicercaComponent implements OnInit, OnDestroy {
  @Input() advancedSearchSelected = false;
  @ViewChild('posizioneSelector') private posizioneSelector!: PositionSelectComponent;
  @ViewChild('classificaInput') classificaInput!: AutoComplete;

  public readonly pageSize = 20;
  public highlighedTerms = '';
  public searchForm: FormGroup<DocumentoSearchForm>;
  public positionDocSearch: Posizione[] = [];
  public columns: DocumentiListColumnNames = ['PosizioneId', 'Protocollo', 'CopiePer','DataDocumento', 'Stato', 'StatoDal', 'StatoUtente', 'Arrivo', 'Oggetto', 'Note', 'ActionButtons'];
  public totalRecords = 0;
  public position: string | null = null;
  public utentiFiltrati: Utente[] = [];
  public utenti: Utente[] = [];
  public sezioniList: Sezione[] = [];
  public sezioniFiltred: { nomeSezione: string }[] = [];
  public isFulltextSearch = false;
  public searchResponse = {
    originalRequest: { pageFirstRow: 0, pageSize: this.pageSize, sortField: 'DataDocumento', sortOrder: -1 }
  } as DocumentiSearchResponse;

  public searchFTResponse = {
    originalRequest: { pageFirstRow: 0, pageSize: this.pageSize, sortField: 'DataDocumento', sortOrder: -1 }
  } as DocumentiFTSearchResponse;

  public AnagraficaIndirizziService = AnagraficaIndirizziService;
  public AnagraficaStatiService = AnagraficaStatiService;
  public AnagraficaClassificheService = AnagraficaClassificheService;
  public AnagraficaQualificheService = AnagraficaQualificheService;
  public AnagraficaSezioniService = AnagraficaSezioniService;
  public AnagraficaUtentiService = AnagraficaUtentiService;

  public autocompleteFilter = autocompleteFilter;

  private routeParamSubscription1?: Subscription;
  private readonly subscriber$ = new Subject<void>();
  constructor(
    private readonly fb: FormBuilder,
    private readonly documentiService: DocumentiService,
    private readonly posizioniService: PosizioniService,
    private readonly sezioniService: AnagraficaSezioniService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly anagraficheService: AnagraficheService,
    private readonly appConfig: AppConfig,
  ) {
    anagraficheService.utenti.getAllArray(u => this.utenti = u);
    this.searchForm = this.fb.group<DocumentoSearchForm>({
      descrizioneFascicolo: new FormControl(''),
      protocollo: new FormControl(''),
      oggetto: new FormControl(''),
      dataDocumento: new FormControl(null),
      dataProtocollazione: new FormControl(null),
      mittente: new FormControl(null),
      protmitt: new FormControl(''),
      stato: new FormControl(null),
      contenuto: new FormControl(''),
      nomeAllegato: new FormControl(''),
      statoUtente: new FormControl(''),
      classifica: new FormControl(null),
      esercitazione: new FormControl(false),
      qualifica: new FormControl(null),
      sezione: new FormControl(null)
    });
  }

  ngOnInit(): void {
    this.getSezioni();
    this.searchForm.controls.qualifica.valueChanges.pipe(
      takeUntil(this.subscriber$),
      tap(() => {
        if (this.searchForm.controls.classifica.value) {
          const classificaData = this.anagraficheService.classifiche.getAsArray().find(e => e.classificaId === this.searchForm.controls.classifica.value?.classificaId);
          if (classificaData) {
            this.searchForm.controls.classifica.setValue(classificaData);
          }
        }
      })
    ).subscribe();

    const params = this.route.snapshot.queryParamMap;
    if (params.has('q')) {
      this.fulltextSearch({ searchTerms: params.get('q') ?? '' });
      return;
    } else {
      this.resetFTResults();
    }

    const lastSearchRequest = this.documentiService.getCachedSearchRequest();
    if (lastSearchRequest) {
      this.updateRequestAndSearch(lastSearchRequest);
      return;
    }

    this.routeParamSubscription1 = this.route.paramMap
      .pipe(
        filter(params => params.has('posizione') && params.get('posizione') !== null),
        switchMap(params => {
          this.position = params.get('posizione');
          if (this.position) {
            return this.posizioniService.getAncestor(this.position);
          } else {
            return of(null);
          }
        })
      )
      .subscribe((response: Posizione[] | null) => {
        if (response) {
          this.positionDocSearch = response;
          this.advancedSearchSelected = true;
          this.searchResponse.results = [];
          this.search();
        }
      });

    this.location.replaceState(this.location.path().split('_')[0], '');
  }

  ngOnDestroy(): void {
    this.routeParamSubscription1?.unsubscribe();

    this.subscriber$.next();
    this.subscriber$.complete();
  }

  public isFascicoloSelected(): boolean {
    return (this.positionDocSearch.length !== 0 && this.positionDocSearch[0] !== null && typeof this.positionDocSearch[0] === 'object');
  }

  public isDescrizioneFascicoloSelected(): boolean {
    return (!!this.searchForm.controls.descrizioneFascicolo.value && this.searchForm.controls.descrizioneFascicolo.value.length !== 0);
  }

  clearForm(): void {
    this.searchForm.reset();
    this.positionDocSearch = [];
    this.posizioneSelector?.clearForm();
    this.searchResponse.results = [];
  }

  clearFulltextSearch(): void {
    this.searchFTResponse.results = [];
  }

  search(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true
    });

    if (!this.searchForm.valid) return;

    const sezioniToSearch = this.sezioniList.filter(sezione => {
      return sezione.nomeSezione.toLowerCase() === this.searchForm.value.sezione?.nomeSezione;
    }).map(sezione => sezione.sezioneId);

    const request = {
      pageFirstRow: 0,
      includeAllegati: true,
      pageSize: this.pageSize,
      sortField: 'DataDocumento',
      sortOrder: -1,
      filters: {
        posizioneId: this.isFascicoloSelected() ? this.calcolaPosizione() : null,
        posizioneDescrizione: !this.isFascicoloSelected() ? this.searchForm.controls.descrizioneFascicolo.value : null,
        protocollo: this.searchForm.controls.protocollo.value,
        oggetto: this.searchForm.controls.oggetto.value,
        dataDocumento: null,
        dataDal: this.searchForm.controls.dataDocumento.value ? this.searchForm.controls.dataDocumento.value[0] : null,
        dataAl: this.dataAl(this.searchForm.controls.dataDocumento),
        dataProtocolloDal: this.searchForm.controls.dataProtocollazione.value ? this.searchForm.controls.dataProtocollazione.value[0] : null,
        dataProtocolloAl: this.dataAl(this.searchForm.controls.dataProtocollazione),
        dataProtocollazione: null,
        mittenteId: this.searchForm.controls.mittente.value?.indirizzoId,
        protocolloMittente: this.searchForm.controls.protmitt.value,
        statoId: this.searchForm.controls.stato.value?.statoId,
        statoUtente: this.searchForm.controls.statoUtente.value,
        classificaId: this.searchForm.controls.classifica.value?.classificaId,
        esercitazione: this.searchForm.controls.esercitazione.value,
        qualificaId: this.searchForm.controls.qualifica.value?.qualificaId,
        sezioneId: sezioniToSearch,
        contenuto: this.searchForm.controls.contenuto.value,
        nomeAllegato: this.searchForm.controls.nomeAllegato.value,
      } as DocumentiSearchFilters
    } as DocumentiSearchRequest;

    this.resetSearchCaches();
    this.updateRequestAndSearch(request);
    this.clearFulltextSearch();
  }

  public fulltextSearch(filters: DocumentiFTSearchFilters) {
    const term = filters.searchTerms;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: term ?? ''
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    if (!term) {
      this.clearFulltextSearch();
    } else {
      this.highlighedTerms = term;
      const request = {
        pageFirstRow: 0, pageSize: this.pageSize, sortField: '', sortOrder: 1,
        filters: {
          ...filters,
          searchTerms: term
        }
      } as DocumentiFTSearchRequest;

      this.updateRequestAndFTSearch(request);
      this.clearForm();
    }
  }

  public FullSearchPage(request: DocumentiFTSearchRequest): void {
    if (typeof request.filters.searchTerms === 'undefined') return;

    this.updateRequestAndFTSearch(request);
  }

  updateRequestAndSearch(request: DocumentiSearchRequest | null): void {
    const mergedRequest = { ...this.searchResponse.originalRequest, ...request };
    this.documentiService.search(mergedRequest, true).subscribe(response => {
      this.isFulltextSearch = false;
      this.searchResponse = response;
      setTimeout(() => {
        const $searchresult = window.document.getElementById('searchresult');
        $searchresult?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }, 100);
    });
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 0) {
      this.advancedSearchSelected = false;
    } else {
      this.advancedSearchSelected = true;
    }
  }

  getSezioni(): void {
    this.sezioniService.getAll().pipe(
      takeUntil(this.subscriber$),
      tap(sezioniResult => {
        this.sezioniList = sezioniResult;
        this.sezioniFiltred = this.filterUniqueSezioni();
      })
    ).subscribe();
  }

  searchSezione(searchValue: { query: string }): void {
    this.sezioniFiltred = [
      ...this.filterUniqueSezioni().filter(sezione => sezione.nomeSezione.toLowerCase() !== searchValue.query.toLowerCase())
    ];
  }

  public useSections(): boolean {
    return this.appConfig.logic.useSections;
  }

  private dataAl(formControl: FormControl<Date[] | null>): Date | null {
    if (!formControl.value) return null;
    const dates = formControl.value;
    return dates[1] ?? dates[0];
  }

  private updateRequestAndFTSearch(request: DocumentiFTSearchRequest): void {
    this.documentiService.searchFullText(request, true).subscribe(response => {
      this.searchFTResponse = response;
      this.isFulltextSearch = true;
      this.totalRecords = response.totalCount;
      setTimeout(() => {
        const $fulltextsearchresult = window.document.getElementById('fulltextsearchresult');
        $fulltextsearchresult?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }, 100);
    });
  }

  private calcolaPosizione(): string {
    let pos = '/';
    if (this.positionDocSearch.length !== 0) {
      this.positionDocSearch.forEach((fasc: Posizione) => {
        if (fasc !== null) {
          pos = fasc.posizioneId;
        }
      });
    }
    return pos;
  }

  private resetFTResults(): void {
    this.searchFTResponse.results = [];
    this.documentiService.resetFTCache();
  }

  private filterUniqueSezioni() {
    const uniqueSezioni = Array.from(new Set(this.sezioniList.map(sezione => sezione.nomeSezione)));  // Rendo uniche le righe in base al nome sezione
    this.sezioniFiltred = uniqueSezioni.map(sezione => {  // Trasformo l'array in oggetto per il componente
      return {
        nomeSezione: sezione,
        value: sezione
      };
    });

    return [...this.sezioniFiltred];
  }

  private resetSearchCaches(): void {
    this.documentiService.resetCache();
    this.documentiService.resetFTCache();
  }
}
